import React, { useEffect, useState } from 'react';import { TrendingUp, TrendingDown, MousePointerClick, BarChart2, Zap } from 'lucide-react';
import { peecAPI } from '../api/peecAPI';
import { FilterBar, FilterConfig, PageHeader, PageShell, SectionCard, TableFrame, Td, Th, Badge } from '../components/PeecUI';
import { LLMIcon } from '../components/LLMIcon';

interface ImpactPageProps {
  workspaceId: string;
}

const ImpactPage: React.FC<ImpactPageProps> = ({ workspaceId }) => {
  const [data, setData] = useState<any>(null);
  const [timeRange, setTimeRange] = useState('全部时间');
  const [channelFilter, setChannelFilter] = useState('全部渠道');

  useEffect(() => {
    peecAPI.getImpact(workspaceId).then(setData).catch(console.error);
  }, [workspaceId]);

  const stat = (label: string, value: string, sub: string, up?: boolean) => (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-medium text-slate-500 mb-1">{label}</div>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-bold text-slate-950">{value}</span>
        <span className={`mb-0.5 flex items-center gap-0.5 text-xs font-medium ${up ? 'text-emerald-600' : 'text-slate-400'}`}>
          {up !== undefined && (up ? <TrendingUp size={12} /> : <TrendingDown size={12} />)}
          {sub}
        </span>
      </div>
    </div>
  );

  const impactData = data ?? {};
  const topPrompts = impactData.top_converting_prompts ?? [
    { prompt: '最佳在线交易平台', conversions: 234, rate: 5.2 },
    { prompt: '500 美元以内交易工具', conversions: 187, rate: 4.8 },
    { prompt: '零佣金交易 App 推荐', conversions: 142, rate: 3.9 },
    { prompt: 'Robinhood vs eToro 对比', conversions: 118, rate: 3.5 },
    { prompt: '最好的股票筛选器 2026', conversions: 97, rate: 3.1 },
  ];

  const channelData = [
    { channel: 'ChatGPT', visits: 4820, conversions: 312, rate: 6.5, trend: 'up' },
    { channel: 'Perplexity', visits: 3210, conversions: 198, rate: 6.2, trend: 'up' },
    { channel: 'Claude', visits: 2780, conversions: 156, rate: 5.6, trend: 'up' },
    { channel: 'Gemini', visits: 2140, conversions: 108, rate: 5.0, trend: 'down' },
    { channel: 'Kimi', visits: 1860, conversions: 87, rate: 4.7, trend: 'up' },
    { channel: 'DeepSeek', visits: 1540, conversions: 72, rate: 4.7, trend: 'up' },
  ];

  const filteredChannels = channelFilter === '全部渠道'
    ? channelData
    : channelData.filter((c) => c.channel === channelFilter);

  const impactFilters: FilterConfig[] = [
    { key: 'time', label: '全部时间', options: ['全部时间', '过去7天', '过去30天', '过去90天'], value: timeRange, onChange: setTimeRange },
    { key: 'channel', label: '全部渠道', options: ['全部渠道', 'ChatGPT', 'Perplexity', 'Claude', 'Gemini', 'Kimi', 'DeepSeek'], value: channelFilter, onChange: setChannelFilter },
  ];

  return (
    <PageShell>
      <PageHeader title="影响力分析" description="追踪 AI 引用带来的品牌曝光、流量和转化贡献" />
      <FilterBar filters={impactFilters} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-6">
        {stat('总转化数', (impactData.total_conversions ?? 1230).toLocaleString(), '+12.4% 本月', true)}
        {stat('转化率', `${impactData.conversion_rate ?? 3.4}%`, '+0.6% 本月', true)}
        {stat('归因营收', `¥${((impactData.revenue_attributed ?? 45600) / 10000).toFixed(1)}万`, '+18.2% 本月', true)}
        {stat('AI 引流访问', '12,890', '+24.7% 本月', true)}
      </div>

      <div className="grid gap-5 xl:grid-cols-[1fr_1fr] mb-5">
        <SectionCard title="转化来源渠道" subtitle="各 AI 平台带来的转化贡献">
          <TableFrame>
            <thead>
              <tr>
                <Th align="left">AI 平台</Th>
                <Th>访问量</Th>
                <Th>转化数</Th>
                <Th>转化率</Th>
                <Th>趋势</Th>
              </tr>
            </thead>
            <tbody>
              {filteredChannels.map((row) => (
                <tr key={row.channel} className="hover:bg-slate-50">
                  <Td align="left">
                    <LLMIcon name={row.channel} size={16} className="font-medium text-slate-900" />
                  </Td>
                  <Td>{row.visits.toLocaleString()}</Td>
                  <Td className="font-semibold">{row.conversions}</Td>
                  <Td>{row.rate}%</Td>
                  <Td>
                    {row.trend === 'up'
                      ? <span className="flex items-center gap-0.5 text-emerald-600 text-xs font-medium"><TrendingUp size={12} /> 上升</span>
                      : <span className="flex items-center gap-0.5 text-rose-500 text-xs font-medium"><TrendingDown size={12} /> 下降</span>
                    }
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        </SectionCard>

        <SectionCard title="高转化提示词" subtitle="带来最多转化的 AI 搜索词">
          <TableFrame>
            <thead>
              <tr>
                <Th align="left">提示词</Th>
                <Th>转化数</Th>
                <Th>转化率</Th>
              </tr>
            </thead>
            <tbody>
              {topPrompts.map((p: any, i: number) => (
                <tr key={i} className="hover:bg-slate-50">
                  <Td align="left">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 font-mono w-4">{i + 1}</span>
                      <span className="text-sm text-slate-900">{p.prompt}</span>
                    </div>
                  </Td>
                  <Td className="font-semibold">{p.conversions}</Td>
                  <Td>
                    <Badge tone={p.rate >= 5 ? 'green' : p.rate >= 4 ? 'blue' : 'slate'}>{p.rate}%</Badge>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        </SectionCard>
      </div>

      <SectionCard title="转化归因概览" subtitle="AI 渠道在整体转化路径中的贡献分析">
        <div className="grid gap-4 p-4 sm:grid-cols-3">
          <div className="rounded-lg bg-slate-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <MousePointerClick size={16} className="text-violet-500" />
              <span className="text-sm font-medium text-slate-700">首次接触归因</span>
            </div>
            <div className="text-2xl font-bold text-slate-950 mb-1">38.4%</div>
            <div className="text-xs text-slate-500">AI 搜索作为转化首触点的占比</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart2 size={16} className="text-blue-500" />
              <span className="text-sm font-medium text-slate-700">多触点归因</span>
            </div>
            <div className="text-2xl font-bold text-slate-950 mb-1">52.1%</div>
            <div className="text-xs text-slate-500">AI 参与至少一个转化触点的占比</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap size={16} className="text-amber-500" />
              <span className="text-sm font-medium text-slate-700">平均转化周期</span>
            </div>
            <div className="text-2xl font-bold text-slate-950 mb-1">3.2天</div>
            <div className="text-xs text-slate-500">从 AI 首次曝光到完成转化的平均时长</div>
          </div>
        </div>
      </SectionCard>
    </PageShell>
  );
};

export default ImpactPage;
